// frontend/src/components/layout/DashboardLayout.js
import React, { useState } from 'react';
import { Home, FileText, CreditCard, Settings, LogOut, Archive, HelpCircle, Menu, X, Shield } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Link, Outlet, useLocation } from 'react-router-dom';
import Footer from './Footer';
import SidebarDatabaseStatus from '../SidebarDatabaseStatus';
import TopBanner from './TopBanner';

const DashboardLayout = () => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const currentPath = location.pathname;
  const supportPaths = new Set([
    '/dashboard/support',
    '/dashboard/help',
    '/dashboard/faq',
    '/dashboard/user-guide',
    '/support/refund-policy',
    '/support/privacy-policy',
    '/support/terms-of-service'
  ]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 bg-white border-b z-30">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <img src="/csc-logo.png" alt="CSC Export Tool Logo" className="h-8" />
        </div>
      </div>

      <div className="flex flex-grow lg:pt-0">
        {/* Sidebar */}
        <div
          className={`fixed h-full bg-white border-r z-20 transition-all duration-300 flex flex-col ${
            isSidebarOpen ? 'w-64 translate-x-0' : '-translate-x-full lg:translate-x-0'
          } lg:w-64`}
        >
          <div className="hidden lg:flex p-6 flex-col items-center flex-shrink-0">
            <img src="/csc-logo.png" alt="CSC Export Tool Logo" className="h-14 mb-3" />
            <h1 className="text-xl font-bold text-gray-900">CSC Export Tool</h1>
          </div>

          <div className="flex-1 overflow-y-auto">
          <nav className="mt-6">
            <NavItem
              icon={<Home />}
              to="/dashboard"
              label="Dashboard"
              active={currentPath === '/dashboard'}
            />
            <NavItem
              icon={<Archive />}
              to="/dashboard/exports"
              label="Create New Export"
              active={currentPath === '/dashboard/exports'}
            />
            <NavItem
              icon={<FileText />}
              to="/dashboard/exports/history"
              label="Export History"
              active={currentPath === '/dashboard/exports/history'}
            />
            <NavItem
              icon={<CreditCard />}
              to="/dashboard/credits"
              label="Credits"
              active={currentPath === '/dashboard/credits' || currentPath.startsWith('/dashboard/credits/')}
            />
            <NavItem
              icon={<Settings />}
              to="/dashboard/settings"
              label="Settings"
              active={currentPath === '/dashboard/settings'}
            />
            <NavItem
              icon={<HelpCircle />}
              to="/dashboard/support"
              label="Support"
              active={supportPaths.has(currentPath)}
            />
            {user?.isAdmin && (
              <NavItem
                icon={<Shield />}
                to="/dashboard/admin"
                label="Admin Dashboard"
                active={currentPath === '/dashboard/admin'}
              />
            )}
          </nav>

          </div>{/* end flex-1 scroll */}

          <div className="flex-shrink-0 w-full">
            {/* Database Status - Desktop only */}
            <div className="hidden lg:block">
              <SidebarDatabaseStatus />
            </div>
            
            <div className="p-4 border-t">
              <button
                onClick={logout}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 w-full px-6 py-3 transition-colors duration-200 rounded-lg hover:bg-gray-50"
              >
                <LogOut size={20} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`flex-grow transition-all duration-300 ${isSidebarOpen ? 'lg:ml-64' : ''}`}>
          <TopBanner />
          <div className="p-4 lg:p-8">
            <div className="max-w-7xl mx-auto">
              <Outlet />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={`transition-all duration-300 ${isSidebarOpen ? 'lg:ml-64' : ''}`}>
        <Footer />
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-10 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
    </div>
  );
};

const NavItem = ({ icon, label, to, active, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center px-6 py-3 text-gray-600 transition-colors duration-200 ${
      active
        ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
        : 'hover:bg-gray-50 hover:text-gray-900'
    }`}
  >
    <span className={`mr-3 ${active ? 'text-blue-600' : ''}`}>{icon}</span>
    <span className="font-medium">{label}</span>
  </Link>
);

export default DashboardLayout;
